/* Usage, cost and quotas over the real API. No X-Lumiq-Tenant header — hard rule 1.
 *
 * Read-only. Setting a limit or a price requires `metering.quota:configure` /
 * `metering.rate:configure`, which no starter role but `admin` holds, and a screen offering those
 * controls to whoever is logged in would misstate who may change what a customer is charged.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { isCostSummary, isQuota, isUsageLine } from '@/shared/api/generated/metering';
import type { CostSummary, Quota, UsageLine } from '@/shared/api/generated/metering';

import { orderUsage } from '../model/metering';
import { getJson } from '@/shared/api/http';

export const USAGE_URL = '/v1/metering/usage';
export const COST_URL = '/v1/metering/cost';
export const QUOTAS_URL = '/v1/metering/quotas';

function itemsOf(body: unknown, url: string): unknown[] {
  if (typeof body !== 'object' || body === null || !('items' in body)) {
    throw new Error(`${url} response has no items`);
  }
  const items: unknown = body.items;
  if (!Array.isArray(items)) {
    throw new Error(`${url} items is not an array`);
  }
  return items;
}

async function fetchUsage(): Promise<UsageLine[]> {
  const items = itemsOf(await getJson(USAGE_URL), USAGE_URL);
  const parsed: UsageLine[] = [];
  for (const item of items) {
    /* Refused whole rather than filtered: a meter silently missing from a usage screen understates
     * the bill, which is the one direction a billing view must never be wrong in. */
    if (!isUsageLine(item)) throw new Error('an item did not match the UsageLine schema');
    parsed.push(item);
  }
  return orderUsage(parsed);
}

async function fetchCost(): Promise<CostSummary> {
  const body = await getJson(COST_URL);
  if (!isCostSummary(body))
    throw new Error('response did not match the CostSummary schema');
  return body;
}

async function fetchQuotas(): Promise<Quota[]> {
  const items = itemsOf(await getJson(QUOTAS_URL), QUOTAS_URL);
  const parsed: Quota[] = [];
  for (const item of items) {
    if (!isQuota(item)) throw new Error('an item did not match the Quota schema');
    parsed.push(item);
  }
  return parsed;
}

export function useUsage(): UseQueryResult<readonly UsageLine[]> {
  return useQuery({ queryKey: ['metering', 'usage'], queryFn: fetchUsage, retry: false });
}

export function useCost(): UseQueryResult<CostSummary> {
  return useQuery({ queryKey: ['metering', 'cost'], queryFn: fetchCost, retry: false });
}

export function useQuotas(): UseQueryResult<readonly Quota[]> {
  return useQuery({
    queryKey: ['metering', 'quotas'],
    queryFn: fetchQuotas,
    retry: false,
  });
}
